import React, { useEffect, useState } from 'react';
import { useObras } from '../context/ObrasContextSupabase';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';

/**
 * Componente que exibe estatísticas sobre obras em vias públicas
 */
const DashboardEstatisticas = () => {
  const { obras, loading } = useObras();
  const [estatisticas, setEstatisticas] = useState({
    total: 0,
    porStatus: [],
    porEmpreiteiro: [],
    valorTotal: 0,
    valorMedio: 0,
    valorPorStatus: [],
    trechos: {
      total: 0,
      totalDistancia: 0,
      mediaPorObra: 0,
      porTipo: []
    },
    progresso: {
      concluido: 0,
      emAndamento: 0,
      percentualConcluido: 0
    }
  });

  // Cores para os gráficos
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];
  
  // Calcular estatísticas quando as obras forem carregadas
  useEffect(() => {
    if (obras.length > 0) {
      calcularEstatisticas();
    }
  }, [obras]);

  // Função para calcular as estatísticas
  const calcularEstatisticas = () => {
    // Total de obras
    const total = obras.length;
    
    // Agrupar por status
    const statusMap = {};
    obras.forEach(obra => {
      statusMap[obra.status] = (statusMap[obra.status] || 0) + 1;
    });
    
    const porStatus = Object.keys(statusMap).map(status => ({
      name: status,
      value: statusMap[status],
      percentual: ((statusMap[status] / total) * 100).toFixed(1)
    }));
    
    // Agrupar por empreiteiro
    const empreiteiroMap = {};
    obras.forEach(obra => {
      empreiteiroMap[obra.empreiteiro] = (empreiteiroMap[obra.empreiteiro] || 0) + 1;
    });
    
    const porEmpreiteiro = Object.keys(empreiteiroMap).map(empreiteiro => ({
      name: empreiteiro,
      value: empreiteiroMap[empreiteiro]
    }));
    
    // Calcular valores
    const valorTotal = obras.reduce((acc, obra) => acc + obra.valor, 0);
    const valorMedio = valorTotal / total;
    
    // Agrupar valor por status
    const valorPorStatusMap = {};
    obras.forEach(obra => {
      valorPorStatusMap[obra.status] = (valorPorStatusMap[obra.status] || 0) + obra.valor;
    });
    
    const valorPorStatus = Object.keys(valorPorStatusMap).map(status => ({
      name: status,
      value: valorPorStatusMap[status]
    }));
    
    // Estatísticas de trechos
    let totalTrechos = 0;
    let totalDistancia = 0;
    const tipoTrechoMap = {};
    
    obras.forEach(obra => {
      if (obra.trechos && obra.trechos.length > 0) {
        totalTrechos += obra.trechos.length;
        
        obra.trechos.forEach(trecho => {
          // Somar distância
          totalDistancia += trecho.distanciaMetros || 0;
          
          // Agrupar por tipo
          tipoTrechoMap[trecho.tipo] = (tipoTrechoMap[trecho.tipo] || 0) + 1;
        });
      }
    });
    
    const porTipo = Object.keys(tipoTrechoMap).map(tipo => ({
      name: tipo,
      value: tipoTrechoMap[tipo]
    }));
    
    // Calcular progresso geral
    let totalItens = 0;
    let itensConcluidos = 0;
    
    obras.forEach(obra => {
      obra.itens.forEach(item => {
        totalItens += item.quantidade;
        itensConcluidos += item.quantidadeMedida || 0;
      });
    });
    
    const percentualConcluido = totalItens > 0 ? (itensConcluidos / totalItens) * 100 : 0;
    
    setEstatisticas({
      total,
      porStatus,
      porEmpreiteiro,
      valorTotal,
      valorMedio,
      valorPorStatus,
      trechos: {
        total: totalTrechos,
        totalDistancia,
        mediaPorObra: total > 0 ? totalTrechos / total : 0,
        porTipo
      },
      progresso: {
        concluido: itensConcluidos,
        emAndamento: totalItens - itensConcluidos,
        percentualConcluido
      }
    });
  };

  // Formatar valores monetários
  const formatarMoeda = (valor) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(valor);
  };

  // Formatar distância
  const formatarDistancia = (metros) => {
    if (metros >= 1000) {
      return `${(metros / 1000).toFixed(2)} km`;
    }
    return `${metros.toFixed(2)} m`;
  };

  return (
    <div className="card">
      <h2>Dashboard de Obras em Vias Públicas</h2>
      
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div style={{ 
            border: '4px solid rgba(0, 0, 0, 0.1)',
            borderLeftColor: '#2e7d32',
            width: '36px',
            height: '36px',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 20px'
          }}></div>
          <p>Carregando estatísticas...</p>
        </div>
      ) : (
        <>
          {/* Cards de estatísticas */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', 
            gap: '20px',
            marginBottom: '30px'
          }}>
            <div style={{ 
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '15px',
              boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
              border: '1px solid #d6d8db',
              borderLeft: '5px solid #6c757d'
            }}>
              <h3 style={{ margin: '0 0 10px 0', color: '#383d41', fontSize: '16px' }}>Total de Obras</h3>
              <div style={{ fontSize: '28px', fontWeight: 'bold', margin: '10px 0', color: '#333' }}>
                {estatisticas.total}
              </div>
              <div style={{ fontSize: '14px', color: '#666' }}>Obras em vias públicas cadastradas</div>
            </div>
            
            <div style={{ 
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '15px',
              boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
              border: '1px solid #c3e6cb',
              borderLeft: '5px solid #28a745'
            }}>
              <h3 style={{ margin: '0 0 10px 0', color: '#155724', fontSize: '16px' }}>Valor Total</h3>
              <div style={{ fontSize: '28px', fontWeight: 'bold', margin: '10px 0', color: '#333' }}>
                {formatarMoeda(estatisticas.valorTotal)}
              </div>
              <div style={{ fontSize: '14px', color: '#666' }}>Investimento total em obras</div>
            </div>
            
            <div style={{ 
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '15px',
              boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
              border: '1px solid #bee5eb',
              borderLeft: '5px solid #17a2b8'
            }}>
              <h3 style={{ margin: '0 0 10px 0', color: '#0c5460', fontSize: '16px' }}>Total de Trechos</h3>
              <div style={{ fontSize: '28px', fontWeight: 'bold', margin: '10px 0', color: '#333' }}>
                {estatisticas.trechos.total}
              </div>
              <div style={{ fontSize: '14px', color: '#666' }}>
                {formatarDistancia(estatisticas.trechos.totalDistancia)} de extensão total
              </div>
            </div>
            
            <div style={{ 
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '15px',
              boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
              border: '1px solid #ffeeba',
              borderLeft: '5px solid #ffc107'
            }}>
              <h3 style={{ margin: '0 0 10px 0', color: '#856404', fontSize: '16px' }}>Progresso Geral</h3>
              <div style={{ fontSize: '28px', fontWeight: 'bold', margin: '10px 0', color: '#333' }}>
                {estatisticas.progresso.percentualConcluido.toFixed(1)}%
              </div>
              <div style={{ height: '8px', backgroundColor: '#e9ecef', borderRadius: '4px', margin: '10px 0', overflow: 'hidden' }}>
                <div 
                  style={{ 
                    height: '100%', 
                    width: `${estatisticas.progresso.percentualConcluido}%`, 
                    backgroundColor: '#ffc107',
                    transition: 'width 0.3s ease'
                  }}
                ></div>
              </div>
              <div style={{ fontSize: '14px', color: '#666' }}>Progresso médio de todas as obras</div>
            </div>
          </div>
          
          {/* Gráficos */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '30px' }}>
            {/* Gráfico de Status */}
            <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '20px', boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)' }}>
              <h3 style={{ marginTop: 0, marginBottom: '15px' }}>Distribuição por Status</h3>
              <div style={{ height: '300px' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={estatisticas.porStatus}
                      cx="50%"
                      cy="50%"
                      labelLine={true}
                      label={({ name, percentual }) => `${name}: ${percentual}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {estatisticas.porStatus.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => [`${value} obras`, 'Quantidade']} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
            
            {/* Gráfico de Valor por Status */}
            <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '20px', boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)' }}>
              <h3 style={{ marginTop: 0, marginBottom: '15px' }}>Valor por Status</h3>
              <div style={{ height: '300px' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={estatisticas.valorPorStatus}
                    margin={{
                      top: 5,
                      right: 30,
                      left: 20,
                      bottom: 5,
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis tickFormatter={(value) => `R$ ${value / 1000}k`} />
                    <Tooltip formatter={(value) => [formatarMoeda(value), 'Valor']} />
                    <Legend />
                    <Bar dataKey="value" name="Valor" fill="#8884d8" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '30px' }}>
            {/* Gráfico de Tipos de Trechos */}
            <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '20px', boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)' }}>
              <h3 style={{ marginTop: 0, marginBottom: '15px' }}>Tipos de Trechos</h3>
              <div style={{ height: '300px' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={estatisticas.trechos.porTipo}
                      cx="50%"
                      cy="50%"
                      labelLine={true}
                      label={({ name, value }) => `${name}: ${value}`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {estatisticas.trechos.porTipo.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => [`${value} trechos`, 'Quantidade']} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
            
            {/* Gráfico de Empreiteiros */}
            <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '20px', boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)' }}>
              <h3 style={{ marginTop: 0, marginBottom: '15px' }}>Obras por Empreiteiro</h3>
              <div style={{ height: '300px' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={estatisticas.porEmpreiteiro}
                    margin={{
                      top: 5,
                      right: 30,
                      left: 20,
                      bottom: 5,
                    }}
                    layout="vertical"
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="name" type="category" width={150} />
                    <Tooltip formatter={(value) => [`${value} obras`, 'Quantidade']} />
                    <Legend />
                    <Bar dataKey="value" name="Quantidade de Obras" fill="#82ca9d" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
          
          {/* Tabela de Obras por Status */}
          <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '20px', boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)' }}>
            <h3 style={{ marginTop: 0, marginBottom: '15px' }}>Resumo por Status</h3>
            
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ padding: '10px', textAlign: 'left', borderBottom: '1px solid #e9ecef', backgroundColor: '#f8f9fa', fontWeight: '600' }}>Status</th>
                  <th style={{ padding: '10px', textAlign: 'center', borderBottom: '1px solid #e9ecef', backgroundColor: '#f8f9fa', fontWeight: '600' }}>Quantidade</th>
                  <th style={{ padding: '10px', textAlign: 'center', borderBottom: '1px solid #e9ecef', backgroundColor: '#f8f9fa', fontWeight: '600' }}>Percentual</th>
                  <th style={{ padding: '10px', textAlign: 'right', borderBottom: '1px solid #e9ecef', backgroundColor: '#f8f9fa', fontWeight: '600' }}>Valor Total</th>
                </tr>
              </thead>
              <tbody>
                {estatisticas.porStatus.map((status, index) => {
                  const valorStatus = estatisticas.valorPorStatus.find(item => item.name === status.name)?.value || 0;
                  return (
                    <tr key={index}>
                      <td style={{ padding: '10px', textAlign: 'left', borderBottom: '1px solid #e9ecef' }}>{status.name}</td>
                      <td style={{ padding: '10px', textAlign: 'center', borderBottom: '1px solid #e9ecef' }}>{status.value}</td>
                      <td style={{ padding: '10px', textAlign: 'center', borderBottom: '1px solid #e9ecef' }}>{status.percentual}%</td>
                      <td style={{ padding: '10px', textAlign: 'right', borderBottom: '1px solid #e9ecef' }}>{formatarMoeda(valorStatus)}</td>
                    </tr>
                  );
                })}
                <tr style={{ backgroundColor: '#f8f9fa', fontWeight: 'bold' }}>
                  <td style={{ padding: '10px', textAlign: 'left', borderBottom: '1px solid #e9ecef' }}>Total</td>
                  <td style={{ padding: '10px', textAlign: 'center', borderBottom: '1px solid #e9ecef' }}>{estatisticas.total}</td>
                  <td style={{ padding: '10px', textAlign: 'center', borderBottom: '1px solid #e9ecef' }}>100%</td>
                  <td style={{ padding: '10px', textAlign: 'right', borderBottom: '1px solid #e9ecef' }}>{formatarMoeda(estatisticas.valorTotal)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )}
      
      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default DashboardEstatisticas;